/* @flow */

import type { $TrackingEvent } from 'CoreTypes/TrackingEvent';
import { ActionTypes as DealerActionTypes } from './dealer';
import { RequestStatus } from 'CoreConstants/RequestStatus';
import { SearchResultsViewMode } from '../components/SearchResults/SearchResultsViewMode';
import { SearchResultsViewModeTracking } from '../components/SearchResults/SearchResultsViewMode';
import { TrackingAttributes } from 'CoreConstants/Tracking';
import { TrackingEventTypes } from 'CoreConstants/Tracking';
import Globals from 'CoreConstants/Globals';
import { setBaseData } from 'CoreUtils/TrackingUtils';
import { trackEvent } from 'CoreUtils/TrackingUtils';
import { getConfigurationMarketProperty } from 'CoreUtils/WidgetUtils';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';
import { INCREASE_SEARCH_COUNT } from './search';
import { SET_DEALERS_LIST_PAGE } from './search';
import { SET_INITIAL_VIEW_MODE } from './viewMode';
import { SET_CURRENT_VIEW_MODE } from './viewMode';

function setWidgetViewModeValue(state: Object, event: $TrackingEvent, viewMode?: string): $TrackingEvent {

  if (viewMode) {
    return {
      ...event,
      page: {
        ...event.page,
        view: viewMode
      }
    };
  }
  else {
    const viewModeCode = state.viewMode.current || state.viewMode.initial;
    const view = viewModeCode === SearchResultsViewMode.LIST_MODE.key ||
      viewModeCode === SearchResultsViewMode.COMBO_MODE_LIST.key ?
      SearchResultsViewModeTracking.LIST : SearchResultsViewModeTracking.MAP;

    return {
      ...event,
      page: {
        ...event.page,
        view
      }
    };
  }
}

function setFilteringValues(state: Object, event: $TrackingEvent): $TrackingEvent {
  const searchLocationTerm = getSearchLocationTermValue({ ...state });
  const searchDealerNameTerm = getSearchDealerNameTermValue({ ...state });
  const searchingByGeolocation = getSearchGeolocationTermValue({ ...state });
  const searchSuggestionTerm = getSuggestionTermValue({ ...state });

  if (searchLocationTerm || searchDealerNameTerm || searchingByGeolocation || Object.keys(state.filters).length > 0) {
    const filter = [];

    if (searchLocationTerm) filter.push({
      name: 'location',
      value: searchLocationTerm
    });

    if (searchDealerNameTerm) filter.push({
      name: 'dealerName',
      value: searchDealerNameTerm
    });

    const filterActivityTypes = state.filters.activityType;
    if (filterActivityTypes && Array.isArray(filterActivityTypes) && filterActivityTypes.length > 0) filter.push({
      name: 'activityType',
      value: [...state.filters.activityType].join(',')
    });

    const filterProductGroups = state.filters.productGroup;
    if (filterProductGroups && Array.isArray(filterProductGroups) && filterProductGroups.length > 0) filter.push({
      name: 'productGroup',
      value: [...state.filters.productGroup].join(',')
    });

    if (state.geolocation.position && searchingByGeolocation) {
      filter.push({
        name: 'geolocation',
        value: true
      });
    }

    if (searchSuggestionTerm) {
      filter.push({
        name: 'autocomplete',
        value: searchSuggestionTerm.label
      });
    }

    const filterSearchRadius = state.filters.searchRadius;
    if (searchLocationTerm && filterSearchRadius &&
      Array.isArray(filterSearchRadius) && filterSearchRadius.length > 0) {

      const distanceUnit = getConfigurationMarketProperty(
        state.configuration, state.settings.market, TemplateMarketPropertyKeys.GEN_DISTANCE_UNIT
      ) || Globals.DEFAULT_DISTANCE_UNIT;

      filter.push({
        name: 'searchRadius',
        value: [...state.filters.searchRadius].map((filter: string) => `${filter}${distanceUnit}`).join(',')
      });
    }


    return {
      ...event,
      page: {
        ...event.page,
        filter
      }
    };
  }
  else return { ...event };
}

function getSearchLocationTermValue(state: Object) {
  return state.search.lastRequestParams &&
    state.search.lastRequestParams.isSearchByLocationActive === true &&
    state.search.lastRequestParams.searchLocationString ?
    state.search.lastRequestParams.searchLocationString : '';
}

function getSearchDealerNameTermValue(state: Object) {
  return state.search.lastRequestParams &&
    state.search.lastRequestParams.isSearchByLocationActive === false &&
    state.search.lastRequestParams.searchNameString ?
    state.search.lastRequestParams.searchNameString : '';
}

function getSearchGeolocationTermValue(state: Object) {
  return state.search.lastRequestParams &&
    state.search.lastRequestParams.forceGeolocation;
}

function getSuggestionTermValue(state: Object) {
  return state.search.lastRequestParams &&
    state.search.lastRequestParams.suggestion;
}

function getSearchPageviewEvent(
  state: Object, dswEvent: $TrackingEvent, page?: ?number, viewMode?: string
): $TrackingEvent {
  let event: $TrackingEvent = {
    ...dswEvent,
    application: {
      ...dswEvent.application,
      componentId: TrackingAttributes.APPLICATION_COMPONENT_ID_DSW
    },
    event: TrackingEventTypes.PAGE_VIEW,
    page: {
      name: TrackingAttributes.PAGE_NAME_SEARCH_RESULTS,
      search: {
        results: state.search.resultsMap.length
      }
    }
  };

  if (page) {

    event = {
      ...event,
      page: {
        ...event.page,
        index: page
      }
    };
  }

  event = setWidgetViewModeValue({ ...state }, { ...event }, viewMode);
  event = setFilteringValues({ ...state }, { ...event });
  return event;
}

export const trackingSearchMiddleware = (store: Object) => (next: Function) => (action: Object) => {

  const state = store.getState().dsw;
  const dswEvent: $TrackingEvent = setBaseData({ ...state });

  if (action.ignoreTracking) return next(action);

  switch (action.type) {

    case INCREASE_SEARCH_COUNT: {
      const viewMode = state.viewMode.current || state.viewMode.initial;
      const page = viewMode === SearchResultsViewMode.LIST_MODE.key ? state.search.resultsListPage : null;
      const event: $TrackingEvent = getSearchPageviewEvent(state, dswEvent, page);
      trackEvent(event);
      break;
    }

    case SET_INITIAL_VIEW_MODE: {
      const viewMode = action.initial;
      if (viewMode === SearchResultsViewMode.LIST_MODE.key ||
        viewMode === SearchResultsViewMode.COMBO_MODE_LIST.key) {
        const event: $TrackingEvent = {
          ...dswEvent,
          application: {
            ...dswEvent.application,
            componentId: TrackingAttributes.APPLICATION_COMPONENT_ID_DSW
          },
          event: TrackingEventTypes.PAGE_VIEW,
          page: {
            name: TrackingAttributes.PAGE_NAME_SEARCH_START
          }
        };
        trackEvent(event);
      }
      break;
    }

    case SET_CURRENT_VIEW_MODE: {

      const viewMode = action.current === SearchResultsViewMode.MAP_MODE.key ?
        SearchResultsViewModeTracking.MAP : SearchResultsViewModeTracking.LIST;

      const currentViewMode = state.viewMode.current || state.viewMode.initial;
      if (action.current !== currentViewMode) {
        const page = viewMode === SearchResultsViewModeTracking.LIST ? state.search.resultsListPage : null;
        const event: $TrackingEvent = getSearchPageviewEvent(state, dswEvent, page, viewMode);
        trackEvent(event);
      }

      break;
    }

    case SET_DEALERS_LIST_PAGE: {
      if (state.search.requestPageStatus === RequestStatus.SUCCESS) {
        const event: $TrackingEvent = getSearchPageviewEvent(state, dswEvent, action.resultsListPage);
        trackEvent(event);
      }
      break;
    }

    case DealerActionTypes.DSW_SET_PREVIEWED_DEALER: {
      const event: $TrackingEvent = {
        ...dswEvent,
        application: {
          ...dswEvent.application,
          componentId: TrackingAttributes.APPLICATION_COMPONENT_ID_DSW
        },
        event: TrackingEventTypes.PAGE_VIEW,
        page: {
          name: TrackingAttributes.PAGE_NAME_DEALER_DETAILS,
          retailerId: action.previewedDealerId
        }
      };
      trackEvent(event);
      break;
    }

    case DealerActionTypes.DSW_UNSET_PREVIEWED_DEALER: {
      const event: $TrackingEvent = getSearchPageviewEvent(state, dswEvent);
      trackEvent(event);
      break;
    }

    case DealerActionTypes.DSW_SET_SELECTED_DEALER: {
      const event: $TrackingEvent = {
        ...dswEvent,
        application: {
          ...dswEvent.application,
          componentId: TrackingAttributes.APPLICATION_COMPONENT_ID_DSW
        },
        event: TrackingEventTypes.CLICK,
        link: {
          name: TrackingAttributes.LINK_NAME_DSW_DEALER_SELECT,
          position: action.position,
          retailerId: action.selectedDealerId
        }
      };
      trackEvent(event);
      break;
    }
  }
  return next(action);
};
